import React from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { Grid3x3, Building2, Users, Settings, LogOut, Moon, Sun, Table2, ShieldCheck, ClipboardCheck } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import useTheme from "../../hooks/useTheme";
import NotificationBell from "./NotificationBell";

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join("");

export default function WorkspaceLayout() {
  const { user, org, isAdmin, logout } = useAuth();
  const { theme, toggle } = useTheme();
  return (
    <div className="ws-shell" data-testid="workspace">
      <aside className="ws-side glass">
        <Link to="/" className="brand" data-testid="brand-link">
          <span className="brand-mark">
            <Grid3x3 size={18} strokeWidth={2.4} aria-hidden="true" />
          </span>
          <span className="brand-text">
            Microsoft <b>License Hub</b>
          </span>
        </Link>
        <div className="ws-org">
          <span className="ws-org-label">Organization</span>
          <b data-testid="ws-org-name">{org?.name}</b>
        </div>
        <nav className="ws-nav" aria-label="Workspace">
          <NavLink to="/app/tenants" data-testid="nav-tenants">
            <Building2 size={16} /> Tenants
          </NavLink>
          <NavLink to="/app/compliance" data-testid="nav-compliance">
            <ClipboardCheck size={16} /> Compliance
          </NavLink>
          {isAdmin && (
            <NavLink to="/app/users" data-testid="nav-users">
              <Users size={16} /> Users
            </NavLink>
          )}
          <NavLink to="/app/settings" data-testid="nav-settings">
            <Settings size={16} /> Settings
          </NavLink>
          <div className="ws-nav-sep" />
          <NavLink to="/" end data-testid="nav-matrix">
            <Table2 size={16} /> License matrix
          </NavLink>
        </nav>
        <div className="ws-user">
          <span className="avatar" aria-hidden="true">
            {initials(user?.name)}
          </span>
          <div className="ws-user-meta">
            <b data-testid="ws-user-name">{user?.name}</b>
            <span>
              <span className={`role-chip ${user?.role}`} data-testid="ws-user-role">
                {user?.role === "admin" ? <ShieldCheck size={10} /> : null} {user?.role}
              </span>
              {user?.mfa_enabled && <span className="mini-chip ok">MFA</span>}
            </span>
          </div>
          <div className="ws-user-actions">
            <NotificationBell />
            <button type="button" className="icon-btn" onClick={toggle} aria-label="Toggle theme" data-testid="theme-toggle">
              {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            <button type="button" className="icon-btn" onClick={logout} aria-label="Sign out" title="Sign out" data-testid="logout-btn">
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </aside>
      <main className="ws-main">
        <Outlet />
      </main>
    </div>
  );
}
